import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2, RefreshCw } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { FinancialWorkbookFilters } from '@polatkan/api/services';

const fmt = (n: number) =>
  new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const currentYear = () =>
  Number(
    new Intl.DateTimeFormat('en', { timeZone: 'Europe/Istanbul', year: 'numeric' }).format(
      new Date()
    )
  );
const yearFilters = (year: number): FinancialWorkbookFilters => ({
  from: `${year}-01-01`,
  to: `${year}-12-31`,
});
const selectClass = 'h-10 w-full rounded-md border border-input bg-background px-3 text-sm';

export function ProductProfitLossReport() {
  const { t } = useTranslation('trading');
  const [filters, setFilters] = useState(() => yearFilters(currentYear()));
  const [applied, setApplied] = useState(filters);
  const [exporting, setExporting] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const options = trpc.tradingFinancialReports.workbookOptions.useQuery(undefined, {
    staleTime: 60000,
  });
  // Download uses the exact preview payload; refresh explicitly to read newer records.
  const report = trpc.tradingFinancialReports.getWorkbook.useQuery(applied, {
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    retry: false,
  });
  const { data } = report;
  const changed = JSON.stringify(filters) !== JSON.stringify(applied);
  const valid = !!filters.from && !!filters.to && filters.from <= filters.to;
  const update = (patch: Partial<FinancialWorkbookFilters>) =>
    setFilters((f) => ({ ...f, ...patch }));
  async function download() {
    if (!data) return;
    setExporting(true);
    setDownloadError('');
    try {
      const { downloadTradingFinancialWorkbook } = await import('@/lib/trading-financial-xlsx');
      await downloadTradingFinancialWorkbook(data);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Excel download failed. Please retry.');
    } finally {
      setExporting(false);
    }
  }
  return (
    <div className="space-y-5">
      <form
        className="bg-card space-y-4 rounded-xl border p-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (!valid) return;
          setDownloadError('');
          if (changed) setApplied(filters);
          else void report.refetch();
        }}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold">
              {t('financialWorkbook.title', 'Financial review / Gelir · Gider · Kâr')}
            </h2>
            <p className="text-muted-foreground text-sm">
              {t(
                'financialWorkbook.subtitle',
                'Preview and download the same financial snapshot, with original currencies and stored exchange rates.'
              )}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setFilters({ ...filters, ...yearFilters(currentYear()) })}
            >
              {t('financialWorkbook.thisYear', 'This year')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setFilters({ ...filters, ...yearFilters(currentYear() - 1) })}
            >
              {t('financialWorkbook.lastYear', 'Last year')}
            </Button>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <label className="space-y-1 text-sm">
            {t('financialWorkbook.from', 'From')}
            <Input
              type="date"
              value={filters.from}
              required
              min="2000-01-01"
              max={filters.to || undefined}
              onChange={(e) => update({ from: e.target.value })}
            />
          </label>
          <label className="space-y-1 text-sm">
            {t('financialWorkbook.to', 'To (inclusive)')}
            <Input
              type="date"
              value={filters.to}
              required
              min={filters.from || '2000-01-01'}
              onChange={(e) => update({ to: e.target.value })}
            />
          </label>
          <label className="space-y-1 text-sm">
            {t('financialWorkbook.currency', 'Original sales currency')}
            <select
              aria-label={t('financialWorkbook.currency', 'Original sales currency')}
              className={selectClass}
              value={filters.currency ?? ''}
              disabled={options.isLoading}
              onChange={(e) => update({ currency: e.target.value || undefined })}
            >
              <option value="">{t('financialWorkbook.allCurrencies', 'All currencies')}</option>
              {options.data?.currencies.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-sm">
            {t('financialWorkbook.customer', 'Customer')}
            <select
              aria-label={t('financialWorkbook.customer', 'Customer')}
              className={selectClass}
              value={filters.customerId ?? ''}
              disabled={options.isLoading}
              onChange={(e) => update({ customerId: e.target.value || undefined })}
            >
              <option value="">{t('financialWorkbook.allCustomers', 'All customers')}</option>
              {options.data?.customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.companyName}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={!valid || report.isFetching || exporting}>
            {report.isFetching ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            {t('financialWorkbook.apply', 'Apply / refresh')}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => void download()}
            disabled={!data || changed || report.isFetching || report.isError || exporting}
          >
            {exporting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            {t('financialWorkbook.download', 'Download styled Excel')}
          </Button>
          {changed && (
            <span className="text-muted-foreground text-sm">
              {t(
                'financialWorkbook.unapplied',
                'Apply the changed filters to update the preview and download.'
              )}
            </span>
          )}
        </div>
      </form>
      {(report.error || options.error || downloadError) && (
        <div
          role="alert"
          className="border-destructive/40 bg-destructive/5 text-destructive rounded-lg border p-4 text-sm"
        >
          {downloadError || report.error?.message || options.error?.message}
        </div>
      )}
      {report.isLoading && (
        <div role="status" className="py-12 text-center">
          <Loader2 className="mx-auto h-6 w-6 animate-spin" />
          <span className="sr-only">Loading report</span>
        </div>
      )}
      {data && (
        <>
          <div className="rounded-xl bg-[#142D43] p-5 text-white">
            <div className="text-xs font-semibold uppercase tracking-widest text-teal-200">
              {data.scope === 'BUSINESS'
                ? t('financialWorkbook.business', 'Recorded business profit & loss')
                : t(
                    'financialWorkbook.margin',
                    'Filtered sales margin — company overhead and FX excluded'
                  )}
            </div>
            <div className="mt-1 text-sm text-slate-200">
              {data.filters.from} → {data.filters.to} ·{' '}
              {data.customerName ?? t('financialWorkbook.allCustomers', 'All customers')} ·{' '}
              {data.filters.currency ?? t('financialWorkbook.allCurrencies', 'All currencies')}
            </div>
            <div className="mt-5 grid gap-5 sm:grid-cols-3">
              {[
                [t('reports.salesRevenue', 'Net sales'), data.summary.salesRevenue],
                [t('reports.costOfSales', 'Cost of sales'), data.summary.costOfSales],
                [t('reports.grossProfit', 'Gross profit'), data.summary.grossProfit],
              ].map(([label, value]) => (
                <div key={label}>
                  <div className="text-sm text-slate-300">{label}</div>
                  <div className="mt-1 text-2xl font-semibold tabular-nums">
                    {fmt(Number(value))} <span className="text-sm font-normal">TRY</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border p-4">
            <h3 className="mb-4 font-semibold">
              {t('financialWorkbook.monthly', 'Monthly performance / TRY')}
            </h3>
            <div className="overflow-x-auto">
              <div
                className="h-72"
                style={{ minWidth: Math.max(600, data.monthly.length * 45) }}
                aria-label="Monthly net sales, cost of sales and gross profit"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.monthly} margin={{ top: 5, right: 5, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis
                      tickFormatter={(v: number) =>
                        new Intl.NumberFormat('en', { notation: 'compact' }).format(v)
                      }
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip formatter={(v: number) => `${fmt(v)} TRY`} />
                    <Legend />
                    <Bar
                      dataKey="salesRevenue"
                      name={t('reports.salesRevenue', 'Net sales')}
                      fill="#142D43"
                    />
                    <Bar
                      dataKey="costOfSales"
                      name={t('reports.costOfSales', 'Cost of sales')}
                      fill="#2374AB"
                    />
                    <Bar
                      dataKey="grossProfit"
                      name={t('reports.grossProfit', 'Gross profit')}
                      fill="#147D64"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <details className="mt-3 text-sm">
              <summary className="text-muted-foreground cursor-pointer">
                {t('financialWorkbook.monthlyTable', 'View monthly figures')}
              </summary>
              <div className="mt-2 overflow-auto">
                <table className="w-full text-right">
                  <thead>
                    <tr>
                      <th className="p-2 text-left">Month</th>
                      <th className="p-2">Net sales TRY</th>
                      <th className="p-2">COGS TRY</th>
                      <th className="p-2">Gross profit TRY</th>
                      <th className="p-2">
                        {data.scope === 'BUSINESS' ? 'Recorded net TRY' : 'Sales margin TRY'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.monthly.map((m) => (
                      <tr key={m.month} className="border-t">
                        <td className="p-2 text-left">{m.month}</td>
                        {[m.salesRevenue, m.costOfSales, m.grossProfit, m.netResult].map((n, i) => (
                          <td key={i} className="p-2 tabular-nums">
                            {fmt(n)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </div>
          <div className="space-y-3">
            {data.summary.categories.map((c) => (
              <details key={c.key} className="rounded-lg border">
                <summary className="cursor-pointer px-4 py-3 font-medium">
                  <span>{c.label}</span>
                  <span className="block tabular-nums sm:float-right">{fmt(c.total)} TRY</span>
                </summary>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <tbody>
                      {c.accounts.map((a) => (
                        <tr key={a.id} className="border-t">
                          <td className="px-4 py-2 font-mono text-xs">{a.code}</td>
                          <td className="px-4 py-2">{a.name}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{fmt(a.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            ))}
          </div>
          <div className="flex flex-wrap justify-between gap-3 rounded-xl border-2 border-[#147D64]/30 p-4 font-semibold">
            <span>
              {data.scope === 'BUSINESS'
                ? t('financialWorkbook.recordedNet', 'Recorded net profit / loss')
                : t('financialWorkbook.salesMargin', 'Sales margin')}
            </span>
            <span className="tabular-nums">{fmt(data.summary.netResult)} TRY</span>
          </div>
          {data.inventory && (
            <div className="rounded-xl border p-4 text-sm">
              <h3 className="font-semibold">
                {t('financialWorkbook.stock', 'Current company-wide stock control')}
              </h3>
              <p className="text-muted-foreground mt-1">
                {t(
                  'financialWorkbook.stockNote',
                  'Current snapshot, not inventory at the selected period end.'
                )}
              </p>
              <div className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
                <span>GL: {fmt(data.inventory.glBalance)} TRY</span>
                <span>
                  FIFO:{' '}
                  {data.inventory.fifoValue == null
                    ? 'Unavailable'
                    : `${fmt(data.inventory.fifoValue)} TRY`}
                </span>
                <span>
                  {t('financialWorkbook.difference', 'Difference')}:{' '}
                  {data.inventory.difference == null
                    ? 'Unavailable'
                    : `${fmt(data.inventory.difference)} TRY`}
                </span>
              </div>
            </div>
          )}
          <details className="rounded-xl border border-amber-300/50 bg-amber-50/40 p-4" open>
            <summary className="cursor-pointer font-semibold">
              {t('financialWorkbook.notes', 'Report notes & source exceptions')} (
              {data.issues.length})
            </summary>
            <ul className="mt-3 space-y-2 text-sm">
              {data.issues.map((i, n) => (
                <li key={`${i.code}-${n}`}>
                  <span className="font-medium">{i.reference ? `${i.reference}: ` : ''}</span>
                  {i.detail}
                </li>
              ))}
            </ul>
          </details>
        </>
      )}
    </div>
  );
}
